/**
 * @file options.ts
 * @description Builds the i18next init options, including the kill-switch shape.
 * @module i18n
 */
import { I18N_NAMESPACE, resources } from "./resources";

import { DEFAULT_UI_LOCALE, SUPPORTED_UI_LOCALES } from "@/constants/locales";

import type { InitOptions } from "i18next";

/**
 * Builds the i18next init options for a given kill-switch state.
 *
 * R7 ("missing translations fall back to English") is satisfied **by config,
 * not code**: `fallbackLng: DEFAULT_UI_LOCALE` plus `returnEmptyString: false`
 * plus `parseMissingKeyHandler` returning the English value means a key absent
 * from a non-English catalog resolves to English, and a raw key name is never
 * what renders.
 *
 * The kill-switch (SE-6507) is the `enabled: false` shape. It deliberately does
 * NOT unmount the provider or swap the translate function for identity — both
 * render raw keys, the exact forbidden failure. It instead narrows the runtime
 * to English only, so the render path is byte-for-byte the same lookup that the
 * fallback already uses. Note this is NOT equivalent to calling
 * `changeLanguage("en_US")`: `supportedLngs` makes English the only reachable
 * language for the lifetime of the instance, so a later language change cannot
 * escape the switch.
 * @param options - Configuration for the built init options
 * @param options.enabled - Kill-switch state; false pins the runtime to English
 * @param options.lng - Initial locale; ignored when the kill-switch is off
 * @returns i18next init options
 */
export function buildInitOptions({
  enabled,
  lng = DEFAULT_UI_LOCALE,
}: {
  readonly enabled: boolean;
  readonly lng?: string;
}): InitOptions {
  return {
    resources,
    lng: enabled ? lng : DEFAULT_UI_LOCALE,
    supportedLngs: enabled
      ? [...SUPPORTED_UI_LOCALES]
      : ([DEFAULT_UI_LOCALE] as string[]),
    fallbackLng: DEFAULT_UI_LOCALE,
    ns: [I18N_NAMESPACE],
    defaultNS: I18N_NAMESPACE,
    // Catalogs are keyed by SupportedLocale enum VALUES ("en_US", not "en"), so
    // i18next must not strip the region to look up a bare-language catalog that
    // does not exist. R11: no new locale taxonomy.
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    // R7: an empty or missing value must never surface as a raw key or a blank.
    returnEmptyString: false,
    returnNull: false,
    // React already escapes; double-escaping mangles apostrophes in fr/it.
    interpolation: { escapeValue: false },
    // No Suspense: all six catalogs are bundled statically, so there is nothing
    // to suspend on, and suspending would stall the tree at mount.
    initImmediate: false,
    react: { useSuspense: false },
    // i18next prints a console.info promo for locize on every init. Harmless,
    // but it lands in dev logs, Jam captures and Sentry breadcrumbs, and noise
    // there costs real triage time later.
    showSupportNotice: false,
  };
}
